import { DOCUMENT } from '@angular/common'
import { Injectable, computed, inject, signal } from '@angular/core'
import { SelectOption } from '../interfaces/select-option'
import { LocalStorageService } from './local-storage.service'

const LOCALE_STORAGE_NAME = 'settings_locale'
const DEFAULT_LOCALE = 'en-GB'
const LOCALE_OPTIONS = [
  { label: 'English', value: 'en-GB' },
  { label: 'Français', value: 'fr-FR' },
  { label: 'Italiano', value: 'it-IT' },
  { label: 'Deutsch', value: 'de-DE' },
  { label: 'Slovak', value: 'sk-SK' },
  { label: 'Español', value: 'es-ES' },
  // { label: 'Nederlands', value: 'nl-NL' },
  { label: 'Polski', value: 'pl-PL' },
  { label: 'Português', value: 'pt-PT' },
  // { label: 'Português Br', value: 'pt-BR' },
  { label: 'Romanian', value: 'ro-RO' },
]

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  private storageSvc = inject(LocalStorageService)
  private document = inject(DOCUMENT)

  private _selectedLocale = signal<string>('')
  public selectedLocale = this._selectedLocale.asReadonly()
  public selectedLanguage = computed(() => this.selectedLocale().split('-')[0])
  public selectedLocaleName = computed(() => {
    const selectedLocale = this._selectedLocale()
    const selectedOption = LOCALE_OPTIONS.find(
      (opt) => opt.value === selectedLocale,
    )
    return selectedOption.label
  })
  public options = signal<SelectOption[]>([])

  constructor() {
    this.options.set(LOCALE_OPTIONS)

    let locale = this.getDefaultLocale()
    const storedLocale = this.storageSvc.getItem(LOCALE_STORAGE_NAME)
    if (
      storedLocale &&
      LOCALE_OPTIONS.findIndex((opt) => opt.value === storedLocale) !== -1
    ) {
      // stored value is a valid option
      locale = storedLocale
    }
    this._selectedLocale.set(locale)
  }

  public selectLocale(locale: string) {
    // find option
    const selectedOption = LOCALE_OPTIONS.find((opt) => opt.value === locale)
    if (!selectedOption) {
      locale = DEFAULT_LOCALE
    }
    // check it's changed
    const currentLocale = this.selectedLocale()
    if (currentLocale === locale) {
      // no change
      return
    }
    this.storageSvc.setItem(LOCALE_STORAGE_NAME, locale)
    this._selectedLocale.set(locale)
    this.document.location!.reload()
  }

  private getDefaultLocale(): string {
    try {
      // language can be 'it-IT' or 'it'
      // navigator.languages is an array of preferred languages, the first one is the most preferred
      const lang = navigator.language || navigator.languages[0]
      let validOption = null
      if (lang && lang.includes('-')) {
        validOption = LOCALE_OPTIONS.find((opt) => opt.value === lang)
      } else if (lang && lang.length === 2) {
        // if it's just a 2-letter language code, append the default country code
        validOption = LOCALE_OPTIONS.find((opt) =>
          opt.value.startsWith(lang + '-'),
        )
      }
      return validOption ? validOption.value : DEFAULT_LOCALE
    } catch (e) {
      console.error('Error getting browser locale, falling back to default:', e)
      return DEFAULT_LOCALE
    }
  }
}
